import { friendlyApiErrorMessage } from 'src/app/services/api-error-notifier.service';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Injectable,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { DataService } from '@dataunveil/ducharts';
import { GridApi, ColDef, GridReadyEvent } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { FilterService } from 'src/app/services/filter.service';
import { WidgetDataGridComponent } from '../data-grid/data-grid.component';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { EditGridDialogComponent } from './edit-grid-dialog/edit-grid-dialog.component';
import { ActionGridService } from './action-grid.service';
import { PopupDeleteComponent } from '../../shared/popup-delete/popup-delete.component';
import { DialogService } from 'src/app/services/dialog.service';
import { NgForm } from '@angular/forms';

@Injectable({ providedIn: 'root' })
@Component({
    selector: 'app-action-grid',
    templateUrl: './action-grid.component.html',
    styleUrls: ['./action-grid.component.scss'],
    standalone: false
})
export class ActionGridComponent implements OnInit, OnDestroy {
  static key = 100613;

  @Input() item: any = {};
  @Output() onSizeChange = new EventEmitter<any>();
  @ViewChild('actionGridDiv', { static: true }) actionGridDiv!: ElementRef;
  @ViewChild('fs') fs!: ElementRef;
  @HostBinding('class.is-fullscreen') isFullscreen = false;

  rowData: any[] = [];
  createFormFields: any[] = [];
  newRecord: any = {};

  loader = true;
  rowStyle: any;
  rowHeight: any = 40;
  gridApi!: GridApi;
  columnDefs: ColDef[] = [];
  autoGroupColumnDef: ColDef<any> = {};
  expandCollapseIcon = 'expand';
  expandOrCollapse = 'Expand All';
  noData = false;
  reqSubcription: Subscription[] = [];
  dataAlignment = 'center';

  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 120,
    resizable: true,
    sortable: false,
    suppressMenu: true,
  };

  constructor(
    private filterService: FilterService,
    private dataService: DataService,
    private httpClient: HttpClient,
    private apiService: ApiService,
    private actionGridService: ActionGridService,
    private dialog: MatDialog
  ) {
    this.reqSubcription.push(
      this.filterService.filterQuery.subscribe(() => {
        this.loader = true;
        this.loadApiData()
      })
    );

    this.rowStyle = {
      padding: '0px 0px',
      'border-bottom': '#f3f3f3 1px solid',
    };
  }

  // Handle fullscreen changes
  @HostListener('fullscreenchange')
  @HostListener('webkitfullscreenchange')
  @HostListener('mozfullscreenchange')
  @HostListener('MSFullscreenChange')
  screenChange() {
    this.isFullscreen = !!document.fullscreenElement;
  }

  ngOnInit(): void {
    const observer = new ResizeObserver((e: any) => {
      this.onSizeChange.emit(e[0].contentRect.height);
    });
    observer.observe(this.actionGridDiv.nativeElement);

    this.loadFormConfig();
    this.loadDataNewGridConfig();
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridApi.addEventListener('rowGroupOpened', this.onRowGroupOpened.bind(this));
  }

  onRowGroupOpened(event: any): void {
    let anyExpanded = false;
    this.gridApi.forEachNode((node) => {
      if (node.group && node.expanded) {
        anyExpanded = true;
      }
    });
    this.expandCollapseIcon = anyExpanded ? 'collapse' : 'expand';
    this.expandOrCollapse = anyExpanded ? 'Collapse All' : 'Expand All';
    this.filterService.expendTree.next({
      key: WidgetDataGridComponent.key,
      value: anyExpanded,
    });
  }

  // ✅ Fix: Renamed to expandCol for consistency
  expandCol(event: any) {
    if (event) {
      this.expandCollapseIcon = 'collapse';
      this.expandOrCollapse = 'Collapse All';
      this.gridApi?.expandAll();
    } else {
      this.expandCollapseIcon = 'expand';
      this.expandOrCollapse = 'Expand All';
      this.gridApi?.collapseAll();
    }
  }

  loadFormConfig() {
    const dataSettingsTab = this.item.config?.tabs?.find(
      (tab: any) => tab.tabName === 'Data Settings'
    );
    const formSection = dataSettingsTab?.sections?.find(
      (sec: any) => sec.Name === 'Create Record Form'
    );

    if (formSection?.fields) {
      this.createFormFields = formSection.fields.map((field: any) => ({
        key: field.key,
        label: field.templateOptions.label,
        placeholder: field.templateOptions.placeHolder,
        type: field.templateOptions.type || 'text',
        required: field.templateOptions.required || false,
        dataType: field.DataType,
      }));

      this.newRecord = {};
      this.createFormFields.forEach((field) => {
        this.newRecord[field.key] =
          field.dataType === 'number' ? null : '';
      });
    } else {
      // Default fallback
      this.createFormFields = [
        {
          key: 'filetype',
          label: 'Filetype',
          placeholder: 'Filetype name...',
          type: 'text',
          required: true,
          dataType: 'string',
        },
        {
          key: 'targetPayment',
          label: 'Target payment',
          placeholder: 'Target payment: $0',
          type: 'number',
          required: true,
          dataType: 'number',
        },
      ];
      this.newRecord = { filetype: '', targetPayment: null };
    }
  }

  loadDataNewGridConfig() {
    this.columnDefs = [
      {
        headerName: 'Filetype',
        field: 'filetype',
        cellStyle: { 'text-align': 'left', 'align-content': 'center', 'border-bottom': 'none' },
      },
      {
        headerName: 'Target payment',
        field: 'targetPayment',
        valueFormatter: (params) =>
          params.value ? `$${parseFloat(params.value).toFixed(2)}` : '$0.00',
        cellStyle: { 'text-align': 'right', 'align-content': 'center', 'border-bottom': 'none' },
      },
      {
        headerName: 'Actions',
        cellStyle: { 'text-align': this.dataAlignment, 'align-content': 'center', 'border-bottom': 'none' },
        cellRenderer: (params:any) => {
          if(params.data.qtr_end_dt) return '';
          return `
            <button id="edit" style="cursor:pointer;background: none;border: none;">
              <svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.75 12.1641H12.75" stroke="#1363DF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M9.75 1.16421C10.0152 0.898997 10.3749 0.75 10.75 0.75C10.9357 0.75 11.1196 0.78658 11.2912 0.857651C11.4628 0.928721 11.6187 1.03289 11.75 1.16421C11.8813 1.29554 11.9855 1.45144 12.0566 1.62302C12.1276 1.7946 12.1642 1.9785 12.1642 2.16421C12.1642 2.34993 12.1276 2.53383 12.0566 2.70541C11.9855 2.87699 11.8813 3.03289 11.75 3.16421L3.41667 11.4975L0.75 12.1642L1.41667 9.49755L9.75 1.16421Z" stroke="#1363DF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <button id="delete" style="cursor:pointer;background: none;border: none;">
              <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.75 3.41602H2.08333H12.75" stroke="#D11044" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.4173 3.41667V12.75C11.4173 13.1036 11.2768 13.4428 11.0268 13.6928C10.7767 13.9429 10.4376 14.0833 10.084 14.0833H3.41732C3.0637 14.0833 2.72456 13.9429 2.47451 13.6928C2.22446 13.4428 2.08398 13.1036 2.08398 12.75V3.41667M4.08398 3.41667V2.08333C4.08398 1.72971 4.22446 1.39057 4.47451 1.14052C4.72456 0.890476 5.0637 0.75 5.41732 0.75H8.08398C8.43761 0.75 8.77674 0.890476 9.02679 1.14052C9.27684 1.39057 9.41732 1.72971 9.41732 2.08333V3.41667" stroke="#D11044" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M5.41602 6.75V10.75" stroke="#D11044" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8.08398 6.75V10.75" stroke="#D11044" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          `
        } 
        ,
        onCellClicked: (params: any) => {

          const target = params.event.target as HTMLElement;

          // Check if the user clicked the Edit button or its SVG children
          if (target.closest('#edit')) {
            this.editRow(params.data);
          }

          // Check if the user clicked the Delete button or its SVG children
          if (target.closest('#delete')) {
            this.confirmationPopup(params.data);
          }

        },
      },
    ];

    // Calculate paymentEarned and paymentAfterCorrections for all rows
    this.rowData = this.rowData.map((row) => ({
      ...row,
      paymentAfterCorrections: row.targetPayment * 0.9,
      paymentEarned: row.targetPayment * 0.85,
    }));

    // If gridApi exists, refresh rows
    if (this.gridApi) {
      this.gridApi.setRowData(this.rowData);
    }
  }

  getRowStyle = (params:any) => {
    if (params.data.qtr_end_dt) {
        return { background: '#f7f7f7ff' };
    }
  };

  confirmationPopup(params:any) {
    let dialogRef = this.dialog.open(PopupDeleteComponent, {
      maxWidth: '90vw',
      panelClass: "du-custom-modalbox",
      data:'Filetype'
    });

    dialogRef.afterClosed().subscribe((value) => {
      if(value == '1') {
        this.deleteRow(params);
      }
    }); 
  }

  fullscreenChanges(fullscreen: any) {
    this.isFullscreen = fullscreen;
  }

  gridOptions: any = {
    getContextMenuItems: () => [
      'copy',
      'copyWithHeaders',
      'paste',
      {
        name: 'Export to Excel',
        action: () => this.exportXls(),
      },
    ],
  };

  exportXls() {
    this.gridApi.exportDataAsExcel({
      fileName: this.item.config['report_heading'] || 'GridExport',
    });
  }

  loadApiData() {
    let obj = {}
    let query = Object.assign({}, this.filterService.baseQuery, obj)

    this.reqSubcription.push(
    this.actionGridService.filetypeGetQuery(query).subscribe((res: any) => {
      this.rowData = res?.length ? res : [];
      this.loader = false;
      this.rowData = this.rowData.map((m: any) => {
        return { ...m, filetype: m.file_type, targetPayment: m.target_amount }
      })
      if (this.gridApi) this.gridApi.setRowData(this.rowData);
    }, err => {
      this.loader = false;
    }))
  }

  createNewRecord(form: NgForm) {
    if (form.invalid) {
      // Mark all fields as touched to show errors
      Object.values(form.controls).forEach(control => control.markAsTouched());
      return;
    }

    // if(this.newRecord.targetPayment==0) {
    //   return;
    // }

    // const targetPaymentStr = String(this.newRecord.targetPayment);

    // if (targetPaymentStr.startsWith('0')) {
    //   this.newRecord.targetPayment = targetPaymentStr.replace(/^0+/, '');
    // }

    const newRow = { ...this.newRecord };
    newRow.paymentAfterCorrections = newRow.targetPayment * 0.9;
    newRow.paymentEarned = newRow.targetPayment * 0.85;

    let obj = {
      target_amount: this.newRecord.targetPayment == 0 ? 0 : (Number(this.newRecord.targetPayment) || 0),
      file_type: this.newRecord.filetype || '',
      updated_by: this.filterService.baseQuery?.employee_key || undefined,
      pymt_calc_addn_file_type_key: null
    }
    let query = Object.assign({}, this.filterService.baseQuery, obj)

    // reset form
    this.createFormFields.forEach(
      (f) => (this.newRecord[f.key] = f.dataType === 'number' ? null : '')
    );

    if (form.valid) {
      // Reset form to clear values & validation states
      form.resetForm();
    }

    this.reqSubcription.push(
    this.actionGridService.filetypeUpdateQuery(query).subscribe((res: any) => {
      this.actionGridService.customSnackBars('File Type Created Successfully', 'success', 'green')
      this.loadApiData()
    }, err => {
      if(err.status==200) {
        // A 200 with a plain-text body lands here because Angular could not
        // parse it as JSON. That body is the backend's own success marker, so
        // it is read as a signal — not shown as failure copy.
        let marker = err?.error?.text || err?.error?.error?.message || err?.error?.message || err?.message || '';
        if(marker == 'File Type Created Successfully') {
          this.actionGridService.customSnackBars(marker, 'success', 'green')
          this.loadApiData()
        } else {
          this.actionGridService.customSnackBars(friendlyApiErrorMessage(err), 'warning', 'yellow')
        }
      } else {
        let error_name = friendlyApiErrorMessage(err);
        this.actionGridService.customSnackBars(error_name, 'warning', 'yellow')
      }
    }))
  }

  editRow(rowData: any) {
    const dialogRef = this.dialog.open(EditGridDialogComponent, {
      width: '400px',
      data: rowData,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.update) {
        this.loadApiData();
      }
      // const index = this.rowData.findIndex((d) => d.filetype === rowData.filetype);
      // if (index > -1) this.rowData[index] = result;
      // this.gridApi.setRowData(this.rowData);
    });
  }

  deleteRow(row: any) {
    if (row.pymt_calc_addn_file_type_key) {
      let obj = { pymt_calc_addn_file_type_key: row.pymt_calc_addn_file_type_key }
      let query = Object.assign({}, this.filterService.baseQuery, obj);
      this.reqSubcription.push(
      this.actionGridService.filetypeDeleteQuery(query).subscribe((res: any) => {
        this.loadApiData()
      }, err => {
        if (err.status == 200) {
          this.loadApiData()
        }
      }))
    }
  }

  getTotalPayout(): number {
    return this.rowData
      .filter(item => item.qtr_end_dt == null) // include only items with null qtr_end_dt
      .reduce((sum, item) => sum + Number(item.targetPayment || 0), 0);
  }

  preventInvalidKeys(event: KeyboardEvent) {
    // Block 'e', 'E', '+', and '-'
    if (['e', 'E', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
  }

  ngOnDestroy(): void {
    this.reqSubcription.forEach((res) => res.unsubscribe());
  }
}
